import { Box, Grid, Typography, IconButton } from "@mui/material";
import React, { FunctionComponent, useState, useEffect, useRef } from "react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

const SectionTitle: FunctionComponent<{
  title: string;
  expand: boolean;
  isCollapsible: boolean;
}> = (props): JSX.Element => {
  const ExpandLessOrMore = () => {
    return props.expand ? (
      <ExpandLessIcon sx={{ color: "#1BA0E9" }} />
    ) : (
      <ExpandMoreIcon sx={{ color: "#1BA0E9" }} />
    );
  };

  return (
    <Box
      sx={{
        p: 2,
        backgroundColor: "#F5F5F5",
        borderRadius: 4,
        mt: 2,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        ":hover": props.isCollapsible
          ? {
              cursor: "pointer",
              backgroundColor: "#E5E5E5",
            }
          : null,
      }}
    >
      <Typography fontSize={18} fontWeight="600" color="#1BA0E9">
        {props.title}
      </Typography>
      {props.isCollapsible && <ExpandLessOrMore />}
    </Box>
  );
};

export const FormSection: FunctionComponent<{
  title: string;
  direction?: "row" | "column";
  children?: React.ReactNode;
  isCollapsible?: boolean;
  defaultOpen?: boolean;
}> = ({
  direction = "row",
  defaultOpen = true,
  isCollapsible = false,
  ...props
}): JSX.Element => {
  const [isOpen, setIsOpen] = useState<boolean>(defaultOpen);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen && contentRef.current) {
      const contentHeight = contentRef.current.scrollHeight;
      contentRef.current.style.height = `${contentHeight}px`;
      contentRef.current.style.transition = "height 0.3s ease-out";
      setTimeout(() => {
        contentRef.current!.style.height = "0px";
      }, 0);
    } else if (isOpen && contentRef.current) {
      contentRef.current.style.height = "0px";
      contentRef.current.style.transition = "height 0.3s ease-in";
      setTimeout(() => {
        contentRef.current!.style.height = `${
          contentRef.current!.scrollHeight
        }px`;
      }, 0);
    }
  }, [isOpen]);

  const handleToggle = () => {
    setIsOpen((prev) => !prev);
  };

  return (
    <Grid container direction="column">
      <Grid item mt={2} onClick={isCollapsible ? handleToggle : undefined}>
        <SectionTitle
          isCollapsible={isCollapsible}
          expand={isOpen}
          title={props.title}
        />
      </Grid>
      <Grid
        mt={2}
        container
        direction={direction}
        ref={contentRef}
        style={{
          overflow: isCollapsible ? "hidden" : "inherit",
          marginBottom: 16,
          gap: 10,
        }}
      >
        {props.children}
      </Grid>
    </Grid>
  );
};
